import React from 'react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RefreshCw } from 'lucide-react';

/**
 * Reusable Loan Form Fields Component
 * Used in both LoansPage and CreditApplicationPage
 * 
 * @param {Object} props
 * @param {Object} props.form - Form state object
 * @param {Function} props.setForm - Form state setter
 * @param {string} props.fieldPrefix - Prefix for field names ('' for LoansPage, 'loan_' for CreditApplicationPage)
 * @param {Function} props.formatEuro - Euro formatting function
 * @param {boolean} props.showProResto - Show Pro Resto section (only in LoansPage)
 * @param {boolean} props.showLenderDetails - Show lender, start date, expiry (for CreditApp)
 * @param {boolean} props.compact - Use compact layout for Credit Applications
 * @param {Function} props.onFetchEuribor - Callback to fetch Euribor rates
 * @param {Function} props.onEuriborTermChange - Callback when Euribor term changes
 * @param {Object} props.euriborRates - Current Euribor rates object
 * @param {boolean} props.fetchingEuribor - Loading state for Euribor fetch
 */
export const LoanFormFields = ({
  form,
  setForm,
  fieldPrefix = '',
  formatEuro,
  showProResto = true,
  showLenderDetails = false,
  compact = false,
  onFetchEuribor,
  onEuriborTermChange,
  euriborRates,
  fetchingEuribor = false
}) => {
  // Helper to get field value with prefix
  const getField = (name) => {
    const key = fieldPrefix ? `${fieldPrefix}${name}` : name;
    return form[key] || '';
  };

  // Helper to set field value with prefix
  const setField = (name, value) => {
    const key = fieldPrefix ? `${fieldPrefix}${name}` : name;
    setForm({ ...form, [key]: value });
  };

  // Handle Euribor term change
  const handleEuriborTermChange = (term) => {
    if (onEuriborTermChange) {
      onEuriborTermChange(term);
    } else {
      setField('euribor_termijn', term);
    }
  };

  // Get rente type field name
  const renteType = getField('rente_type');

  // Calculate effective rate for display
  const getCalculatedRate = () => {
    if (renteType === 'variabel') {
      const euribor = parseFloat(getField('euribor_waarde')) || 0;
      const opslag = parseFloat(getField('opslag_rente_var')) || 0;
      return (euribor + opslag).toFixed(2);
    } else if (renteType === 'vast') {
      const irs = parseFloat(getField('irs_waarde')) || 0;
      const opslag = parseFloat(getField('opslag_rente_vast')) || 0;
      return (irs + opslag).toFixed(2);
    }
    return null;
  };

  return (
    <>
      {/* Lender Details Section - Only for Credit Applications */}
      {showLenderDetails && (
        <div className="space-y-4 p-4 rounded-lg bg-slate-800/50 border border-slate-600">
          <Label className="text-slate-300 font-medium text-base">Leningstructuur Details</Label>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">Geldverstrekker</Label>
              <Input
                value={getField('lender_name')}
                onChange={(e) => setField('lender_name', e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
                placeholder="Naam bank/financier"
                data-testid="lender-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">Ingangsdatum</Label>
              <Input
                type="date"
                value={getField('start_date')}
                onChange={(e) => setField('start_date', e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
                data-testid="start-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">Looptijd (jaren)</Label>
              <Select value={getField('expiry_years') || '5'} onValueChange={(value) => setField('expiry_years', value)}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="3" className="text-white hover:bg-slate-800">3 jaar</SelectItem>
                  <SelectItem value="5" className="text-white hover:bg-slate-800">5 jaar</SelectItem>
                  <SelectItem value="7" className="text-white hover:bg-slate-800">7 jaar</SelectItem>
                  <SelectItem value="10" className="text-white hover:bg-slate-800">10 jaar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">Rente Type</Label>
              <Select value={renteType || 'effectief'} onValueChange={(value) => setField('rente_type', value)}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="effectief" className="text-white hover:bg-slate-800">Effectieve Rente (simpel)</SelectItem>
                  <SelectItem value="variabel" className="text-white hover:bg-slate-800">Variabel (Euribor + opslag)</SelectItem>
                  <SelectItem value="vast" className="text-white hover:bg-slate-800">Vast (IRS + opslag)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Conditional Interest Rate Fields */}
          {renteType === 'variabel' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-3 rounded bg-blue-900/20 border border-blue-800/50">
              <div className="space-y-2">
                <Label className="text-slate-400 text-sm">Euribor Termijn</Label>
                <Select value={getField('euribor_termijn')} onValueChange={handleEuriborTermChange}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                    <SelectValue placeholder="Selecteer" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="1M" className="text-white hover:bg-slate-800">1 Maand</SelectItem>
                    <SelectItem value="3M" className="text-white hover:bg-slate-800">3 Maanden</SelectItem>
                    <SelectItem value="6M" className="text-white hover:bg-slate-800">6 Maanden</SelectItem>
                    <SelectItem value="12M" className="text-white hover:bg-slate-800">12 Maanden</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400 text-sm flex items-center gap-2">
                  Euribor Waarde (%)
                  {onFetchEuribor && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={onFetchEuribor}
                      disabled={fetchingEuribor}
                      className="h-6 px-2 text-xs text-blue-400 hover:text-blue-300"
                    >
                      <RefreshCw className={`h-3 w-3 mr-1 ${fetchingEuribor ? 'animate-spin' : ''}`} />
                      {fetchingEuribor ? 'Laden...' : 'ECB Ophalen'}
                    </Button>
                  )}
                </Label>
                <Input
                  type="number"
                  value={getField('euribor_waarde')}
                  onChange={(e) => setField('euribor_waarde', e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white"
                  step="0.001"
                  placeholder="bijv. 3.5"
                />
                {euriborRates && getField('euribor_termijn') && euriborRates[getField('euribor_termijn')] && (
                  <span className="text-xs text-slate-500">
                    ECB {euriborRates[getField('euribor_termijn')].date}: {euriborRates[getField('euribor_termijn')].rate}%
                  </span>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400 text-sm">Opslag (%)</Label>
                <Input
                  type="number"
                  value={getField('opslag_rente_var')}
                  onChange={(e) => setField('opslag_rente_var', e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white"
                  step="0.01"
                  placeholder="bijv. 1.5"
                />
              </div>
              <div className="md:col-span-3 p-2 rounded bg-slate-700/50 flex justify-between items-center">
                <span className="text-slate-400 text-sm">Effectieve Rente (berekend):</span>
                <span className="text-[#b4976f] font-semibold">{getCalculatedRate()}%</span>
              </div>
            </div>
          )}

          {renteType === 'vast' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-3 rounded bg-emerald-900/20 border border-emerald-800/50">
              <div className="space-y-2">
                <Label className="text-slate-400 text-sm">IRS Referentie</Label>
                <Input
                  value={getField('irs_referentie')}
                  onChange={(e) => setField('irs_referentie', e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white"
                  placeholder="bijv. 5-jaars IRS"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400 text-sm">IRS Waarde (%)</Label>
                <Input
                  type="number"
                  value={getField('irs_waarde')}
                  onChange={(e) => setField('irs_waarde', e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white"
                  step="0.001"
                  placeholder="bijv. 2.5"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400 text-sm">Opslag (%)</Label>
                <Input
                  type="number"
                  value={getField('opslag_rente_vast')}
                  onChange={(e) => setField('opslag_rente_vast', e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white"
                  step="0.01"
                  placeholder="bijv. 1.5"
                />
              </div>
              <div className="md:col-span-3 p-2 rounded bg-slate-700/50 flex justify-between items-center">
                <span className="text-slate-400 text-sm">Effectieve Rente (berekend):</span>
                <span className="text-[#b4976f] font-semibold">{getCalculatedRate()}%</span>
              </div>
            </div>
          )}

          {renteType === 'effectief' && (
            <div className="p-3 rounded bg-amber-900/20 border border-amber-800/50">
              <div className="space-y-2 max-w-xs">
                <Label className="text-slate-400 text-sm">Effectieve Rente (%)</Label>
                <Input
                  type="number"
                  value={getField('effectieve_rente')}
                  onChange={(e) => setField('effectieve_rente', e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white"
                  step="0.01"
                  placeholder="bijv. 4.5"
                  data-testid="effectieve-rente-input"
                />
              </div>
            </div>
          )}

          {/* Covenanten Section */}
          <div className="mt-4">
            <Label className="text-slate-300 font-medium text-sm mb-3 block">Covenanten &amp; Ratio&apos;s (optioneel)</Label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">LTV Max (%)</Label>
                <Input
                  type="number"
                  value={getField('ltv_max')}
                  onChange={(e) => setField('ltv_max', e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white text-sm"
                  min="0"
                  max="100"
                  placeholder="bijv. 75"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">ICR Min</Label>
                <Input
                  type="number"
                  value={getField('icr_min')}
                  onChange={(e) => setField('icr_min', e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white text-sm"
                  min="0"
                  step="0.01"
                  placeholder="bijv. 2.0"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">DSCR Min</Label>
                <Input
                  type="number"
                  value={getField('dscr_min')}
                  onChange={(e) => setField('dscr_min', e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white text-sm"
                  min="0"
                  step="0.01"
                  placeholder="bijv. 1.2"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">Debt Yield Min (%)</Label>
                <Input
                  type="number"
                  value={getField('debt_yield_min')}
                  onChange={(e) => setField('debt_yield_min', e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white text-sm"
                  min="0"
                  placeholder="bijv. 8"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pro Resto Section - Only for LoansPage */}
      {showProResto && (
        <div className="space-y-3 p-4 rounded-lg bg-slate-800/50 border border-slate-600">
          <Label className="text-slate-300 font-medium text-base">Hoofdsom &amp; Aflossing</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">Initiële Hoofdsom (€) *</Label>
              <Input
                type="number"
                value={getField('initiele_hoofdsom')}
                onChange={(e) => setField('initiele_hoofdsom', e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
                min="0"
                step="0.01"
                placeholder="Oorspronkelijk leningbedrag"
                required
                data-testid="loan-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">Aflossingspercentage (%) *</Label>
              <Input
                type="number"
                value={getField('aflossingspercentage')}
                onChange={(e) => setField('aflossingspercentage', e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
                min="0"
                max="100"
                step="0.01"
                placeholder="Jaarlijkse aflossing %"
                required
                data-testid="repayment-rate-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">Cumulatief Afgelost (€)</Label>
              <Input
                type="number"
                value={getField('afgelost_bedrag')}
                onChange={(e) => setField('afgelost_bedrag', e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
                min="0"
                step="0.01"
                placeholder="Tot nu toe afgelost"
              />
            </div>
          </div>
          {/* Calculated Pro Resto Hoofdsom - based on today's date */}
          {formatEuro && (
            <div className="mt-2 p-3 rounded bg-slate-700/50 flex justify-between items-center">
              <span className="text-slate-400 text-sm">Pro Resto Hoofdsom (per vandaag):</span>
              <span className="text-[#b4976f] font-semibold text-lg">
                {formatEuro((parseFloat(getField('initiele_hoofdsom')) || 0) - (parseFloat(getField('afgelost_bedrag')) || 0))}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Rente Structuur Section - Only for LoansPage (not compact mode) */}
      {!compact && !showLenderDetails && (
        <div className="space-y-3 p-4 rounded-lg bg-slate-800/50 border border-slate-600">
          <Label className="text-slate-300 font-medium text-base">Rente Structuur</Label>
          
          <div className="space-y-2">
            <Label className="text-slate-400 text-sm">Rente Type</Label>
            <Select value={renteType || 'effectief'} onValueChange={(value) => setField('rente_type', value)}>
              <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                <SelectItem value="effectief" className="text-white hover:bg-slate-800">Effectieve Rente (simpel)</SelectItem>
                <SelectItem value="variabel" className="text-white hover:bg-slate-800">Variabel (Euribor + opslag)</SelectItem>
                <SelectItem value="vast" className="text-white hover:bg-slate-800">Vast (IRS + opslag)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Variabele Rente velden */}
          {renteType === 'variabel' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-3 rounded bg-blue-900/20 border border-blue-800/50">
              <div className="space-y-2">
                <Label className="text-slate-400 text-sm">Euribor Termijn</Label>
                <Select value={getField('euribor_termijn')} onValueChange={(value) => setField('euribor_termijn', value)}>
                  <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                    <SelectValue placeholder="Selecteer" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="1M" className="text-white hover:bg-slate-800">1 Maand</SelectItem>
                    <SelectItem value="3M" className="text-white hover:bg-slate-800">3 Maanden</SelectItem>
                    <SelectItem value="6M" className="text-white hover:bg-slate-800">6 Maanden</SelectItem>
                    <SelectItem value="12M" className="text-white hover:bg-slate-800">12 Maanden</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400 text-sm">Euribor Waarde (%)</Label>
                <Input
                  type="number"
                  value={getField('euribor_waarde')}
                  onChange={(e) => setField('euribor_waarde', e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white"
                  step="0.001"
                  placeholder="bijv. 3.5"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400 text-sm">Opslag (%)</Label>
                <Input
                  type="number"
                  value={getField('opslag_rente_var')}
                  onChange={(e) => setField('opslag_rente_var', e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white"
                  step="0.01"
                  placeholder="bijv. 1.5"
                />
              </div>
              <div className="md:col-span-3 p-2 rounded bg-slate-700/50 flex justify-between items-center">
                <span className="text-slate-400 text-sm">Effectieve Rente (berekend):</span>
                <span className="text-[#b4976f] font-semibold">{getCalculatedRate()}%</span>
              </div>
            </div>
          )}

          {/* Vaste Rente velden */}
          {renteType === 'vast' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-3 rounded bg-emerald-900/20 border border-emerald-800/50">
              <div className="space-y-2">
                <Label className="text-slate-400 text-sm">IRS Referentie</Label>
                <Input
                  value={getField('irs_referentie')}
                  onChange={(e) => setField('irs_referentie', e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white"
                  placeholder="bijv. 5-jaars IRS"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400 text-sm">IRS Waarde (%)</Label>
                <Input
                  type="number"
                  value={getField('irs_waarde')}
                  onChange={(e) => setField('irs_waarde', e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white"
                  step="0.001"
                  placeholder="bijv. 2.5"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400 text-sm">Opslag (%)</Label>
                <Input
                  type="number"
                  value={getField('opslag_rente_vast')}
                  onChange={(e) => setField('opslag_rente_vast', e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white"
                  step="0.01"
                  placeholder="bijv. 1.5"
                />
              </div>
              <div className="md:col-span-3 p-2 rounded bg-slate-700/50 flex justify-between items-center">
                <span className="text-slate-400 text-sm">Effectieve Rente (berekend):</span>
                <span className="text-[#b4976f] font-semibold">{getCalculatedRate()}%</span>
              </div>
            </div>
          )}

          {/* Effectieve Rente (simpel) */}
          {renteType === 'effectief' && (
            <div className="p-3 rounded bg-amber-900/20 border border-amber-800/50">
              <div className="space-y-2 max-w-xs">
                <Label className="text-slate-400 text-sm">Effectieve Rente (%) *</Label>
                <Input
                  type="number"
                  value={getField('effectieve_rente')}
                  onChange={(e) => setField('effectieve_rente', e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white"
                  step="0.01"
                  placeholder="bijv. 4.5"
                  required
                  data-testid="interest-rate-input"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Covenanten Section - Only for LoansPage */}
      {!compact && !showLenderDetails && (
        <div className="space-y-3 p-4 rounded-lg bg-slate-800/50 border border-slate-600">
          <Label className="text-slate-300 font-medium text-base">Covenanten &amp; Ratio&apos;s</Label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">LTV Max (%)</Label>
              <Input
                type="number"
                value={getField('ltv_max')}
                onChange={(e) => setField('ltv_max', e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
                min="0"
                max="100"
                step="0.01"
                placeholder="bijv. 75"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">ICR Min</Label>
              <Input
                type="number"
                value={getField('icr_min')}
                onChange={(e) => setField('icr_min', e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
                min="0"
                step="0.01"
                placeholder="bijv. 2.0"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">DSCR Min</Label>
              <Input
                type="number"
                value={getField('dscr_min')}
                onChange={(e) => setField('dscr_min', e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
                min="0"
                step="0.01"
                placeholder="bijv. 1.2"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">Debt Yield Min (%)</Label>
              <Input
                type="number"
                value={getField('debt_yield_min')}
                onChange={(e) => setField('debt_yield_min', e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
                min="0"
                step="0.01"
                placeholder="bijv. 8"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">Rapportagedatum</Label>
              <Input
                type="date"
                value={getField('rapportagedatum')}
                onChange={(e) => setField('rapportagedatum', e.target.value)}
                className="bg-slate-900 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-sm">Meetfrequentie</Label>
              <Select value={getField('meetfrequentie') || 'jaar'} onValueChange={(value) => setField('meetfrequentie', value)}>
                <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="kwartaal" className="text-white hover:bg-slate-800">Kwartaal</SelectItem>
                  <SelectItem value="halfjaar" className="text-white hover:bg-slate-800">Halfjaar</SelectItem>
                  <SelectItem value="jaar" className="text-white hover:bg-slate-800">Jaar</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LoanFormFields;
